import { useEffect, useState } from "react";
import { syncScore, fetchBoard, deviceId } from "./board.js";
import { t } from "./i18n.js";
import AdSlot from "./AdSlot.jsx";

/* GLOBAL leaderboard (Firebase RTDB): everyone's scores, one entry per device.
   Your own entry is synced just before loading the list. */
export default function Leaderboard({ onExit }) {
  const [rows, setRows] = useState(null);   // null = loading, [] = empty
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      await syncScore();                     // make sure our own entry is fresh
      try {
        const board = await fetchBoard();
        if (alive) setRows(board);
      } catch {
        if (alive) { setOffline(true); setRows([]); }
      }
    })();
    return () => { alive = false; };
  }, []);

  const me = deviceId();
  // top-3 faar guld/soelv/bronze-farvede placeringstal (ingen emoji-medaljer)
  const rankClass = (i) => (i === 0 ? " gold" : i === 1 ? " silver" : i === 2 ? " bronze" : "");

  return (
    <div className="lb-root">
      <style>{css}</style>
      <header className="lb-head">
        <button className="lb-back" onClick={onExit}>{t("back")}</button>
        <h1>{t("lb_title")}</h1>
        <span className="lb-note">{t("lb_note")}</span>
      </header>

      <div className="lb-board">
        <div className="lb-row lb-header">
          <span className="lb-rank"></span>
          <span className="lb-nm">{t("lb_name")}</span>
          <span className="lb-c"><img className="lb-ico" src="/images/coin.png" alt={t("ex_coin_one")} /></span>
          <span className="lb-c"><img className="lb-ico" src="/images/trophy.png?v=2" alt={t("trophy")} /></span>
          <span className="lb-c"><img className="lb-ico" src="/images/hangtime-teddy.png" alt={t("lb_name")} /></span>
          <span className="lb-c lb-tot">{t("lb_total")}</span>
        </div>
        {rows === null && <div className="lb-empty">{t("lb_loading")}</div>}
        {rows !== null && offline && <div className="lb-empty">{t("lb_offline")}</div>}
        {rows !== null && !offline && rows.length === 0 && <div className="lb-empty">{t("lb_empty")}</div>}
        {(rows || []).map((r, i) => (
          <div key={r.id} className={"lb-row" + (r.id === me ? " me" : "")}>
            <span className={"lb-rank" + rankClass(i)}>{i + 1}</span>
            <span className="lb-nm">{r.name}</span>
            <span className="lb-c">{r.coins}</span>
            <span className="lb-c">{r.trophies}</span>
            <span className="lb-c">{r.plush}</span>
            <span className="lb-c lb-tot">{r.total}</span>
          </div>
        ))}
      </div>
      <AdSlot name="leaderboard" height={100} />
    </div>
  );
}

const css = `
.lb-root{
  min-height:100vh; background:#152a10;
  background-image:
    radial-gradient(60% 34% at 18% 12%, rgba(62,112,46,.45) 0%, rgba(0,0,0,0) 100%),
    radial-gradient(50% 30% at 82% 30%, rgba(54,98,42,.40) 0%, rgba(0,0,0,0) 100%),
    radial-gradient(55% 32% at 30% 78%, rgba(46,88,36,.35) 0%, rgba(0,0,0,0) 100%),
    radial-gradient(120% 90% at 50% -12%, #33582a 0%, #1d3616 46%, #0f1e0a 100%);
  font-family:'Nunito',system-ui,sans-serif; color:#F6EED9;
  display:flex; flex-direction:column; align-items:center; padding-bottom:30px;
}
.lb-head{
  width:min(96%,520px); display:flex; align-items:center; justify-content:space-between;
  gap:8px; padding:14px 4px 10px;
}
.lb-head h1{
  font-family:'Alfa Slab One',Georgia,serif; font-size:clamp(18px,5.4vw,30px);
  margin:0; letter-spacing:2px;
  background:linear-gradient(180deg,#f7e08a 18%,#d4a943 55%,#9a6c1c 100%);
  -webkit-background-clip:text; background-clip:text; color:transparent;
  filter:drop-shadow(0 2px 1px rgba(0,0,0,.55));
}
.lb-back{
  background:linear-gradient(#6b452a,#4a2c16); color:#f2e7c9;
  border:1px solid #c9a24b; border-radius:9px;
  font-family:inherit; font-weight:800; font-size:13px; padding:7px 11px; cursor:pointer;
  box-shadow:0 3px 0 #2c1a0e, inset 0 1px 0 rgba(255,255,255,.15);
  white-space:nowrap; flex-shrink:0;
}
.lb-note{
  font-weight:800; font-size:11px; color:#f2dc9a; white-space:nowrap;
  background:rgba(0,0,0,.25); border:1px solid rgba(214,178,94,.6);
  padding:5px 10px; border-radius:20px;
}
.lb-board{
  width:min(96%,520px);
  background:linear-gradient(158deg, #f8f1de 0%, #ece0c2 70%, #ddceaa 100%);
  border-radius:16px; padding:8px 10px 10px; color:#3c2c14;
  border:6px solid transparent;
  background-clip:padding-box;
  box-shadow:0 0 0 6px #6a4423, 0 0 0 7px #c9a24b, inset 0 2px 3px rgba(255,255,255,.8), 0 14px 30px rgba(0,0,0,.5);
}
.lb-row{
  display:grid; grid-template-columns:34px 1fr 44px 44px 44px 56px;
  align-items:center; padding:9px 4px; font-weight:800; font-size:14.5px;
  border-bottom:1px dashed #cbbf9c;
}
.lb-row:last-child{border-bottom:none;}
.lb-row:nth-child(odd):not(.lb-header){background:rgba(122,92,51,.08); border-radius:6px;}
.lb-board .lb-row.me{
  background:linear-gradient(#fff8dd,#ffedb8);
  border-radius:10px; border-bottom-color:transparent;
  box-shadow:inset 0 0 0 2px #d9b05e, 0 0 12px rgba(255,205,80,.45);
}
.lb-header{font-size:12px; letter-spacing:.5px; color:#7a5c33; border-bottom:2px solid #cbbf9c;}
.lb-rank{text-align:center; font-size:16px;}
.lb-rank.gold,.lb-rank.silver,.lb-rank.bronze{
  font-family:'Alfa Slab One',Georgia,serif; font-size:13px; color:#fff;
  width:26px; height:26px; line-height:26px; border-radius:50%;
  justify-self:center; text-shadow:0 1px 1px rgba(0,0,0,.35);
}
.lb-rank.gold{
  background:radial-gradient(circle at 35% 30%, #ffe9a0, #e8b83a 55%, #a87914);
  box-shadow:inset 0 2px 3px rgba(255,255,255,.55), inset 0 -3px 4px rgba(0,0,0,.3), 0 0 10px rgba(255,205,80,.6);
}
.lb-rank.silver{
  background:radial-gradient(circle at 35% 30%, #f4f7fa, #b9c0c9 55%, #7e868e);
  box-shadow:inset 0 2px 3px rgba(255,255,255,.6), inset 0 -3px 4px rgba(0,0,0,.3), 0 2px 4px rgba(0,0,0,.3);
}
.lb-rank.bronze{
  background:radial-gradient(circle at 35% 30%, #f0c9a0, #c98950 55%, #8a5426);
  box-shadow:inset 0 2px 3px rgba(255,255,255,.5), inset 0 -3px 4px rgba(0,0,0,.3), 0 2px 4px rgba(0,0,0,.3);
}
.lb-nm{overflow:hidden; text-overflow:ellipsis; white-space:nowrap;}
.lb-c{text-align:center;}
.lb-ico{height:22px; width:auto; vertical-align:middle; filter:drop-shadow(0 1px 2px rgba(0,0,0,.3));}
.lb-tot{
  font-family:'Alfa Slab One',Georgia,serif; color:#7e1f1a; font-size:16px;
}
.lb-empty{text-align:center; padding:26px 0; font-weight:800; opacity:.65;}
/* narrow phones: tighter columns so the board always fits */
@media (max-width:420px){
  .lb-board{padding:6px 6px 8px;}
  .lb-row{grid-template-columns:26px 1fr 34px 34px 34px 44px; font-size:13px; padding:8px 3px;}
  .lb-rank{font-size:14px;}
  .lb-tot{font-size:14px;}
  .lb-header{font-size:11px;}
  .lb-ico{height:18px;}
}
`;
